// Flow formulas of the sections (several gauges, rising / falling factors, delays). The code lives with the Cloud
// Functions, which write the public levels with it; the admin Station tab uses the same code for its previews, so both
// always agree.
export * from "../../functions/includes/flowFormulas.js";
import { HOUR_MS, TREND_WINDOW_MS, flowAt, normalizeTerm } from "../../functions/includes/flowFormulas.js";

export const ACCURACY_LABELS = {
  high: "Élevée",
  medium: "Moyenne",
  low: "Faible",
  unknown: "Non évaluée",
};

// Lines of the formulas on the charts: no level colour (red, green, blue) and no gold (docs/BRAND.md)
export const COMPARISON_COLORS = ["#7E57C2", "#D9622B", "#C2185B", "#5D7A2E", "#6D4C41", "#455A64"];

export function getAccuracyLabel(accuracy) {
  return ACCURACY_LABELS[accuracy] || ACCURACY_LABELS.unknown;
}

const formatNumber = (value) => String(Math.round(Number(value) * 1000) / 1000).replace(".", ",");

// The readable formula in pieces, so the Station tab can put a hover on each station: { kind: "text", text } or
// { kind: "gauge", text, termIndex } (index in formula.terms). Joined, the pieces read e.g.
// "0,8 × 080101 − (1,2↑ / 1↓) × 1-7294 (il y a 2 h)": a station with different rising and falling factors shows both.
export function formulaParts(formula) {
  const terms = (formula && Array.isArray(formula.terms) ? formula.terms : [])
    .map((term, termIndex) => ({ term, termIndex }))
    .filter(({ term }) => term.gauge_id);
  if (!terms.length) {
    return [{ kind: "text", text: "Aucune station" }];
  }
  const parts = [];
  terms.forEach(({ term, termIndex }, index) => {
    const sign = term.operator === "-" ? "− " : index === 0 ? "" : "+ ";
    const up = Number(term.factor_up);
    const down = term.factor_down === null || term.factor_down === undefined ? up : Number(term.factor_down);
    const factor = up === down ? formatNumber(up) : `(${formatNumber(up)}↑ / ${formatNumber(down)}↓)`;
    const lag = Number(term.lag_hours) > 0 ? ` (il y a ${formatNumber(term.lag_hours)} h)` : "";
    parts.push({ kind: "text", text: `${index === 0 ? "" : " "}${sign}${factor} × ` });
    parts.push({ kind: "gauge", text: term.gauge_station_name || term.gauge_id, termIndex });
    if (lag) parts.push({ kind: "text", text: lag });
  });
  const offset = Number(formula.offset) || 0;
  if (offset) {
    parts.push({ kind: "text", text: ` ${offset < 0 ? "−" : "+"} ${formatNumber(Math.abs(offset))}` });
  }
  return parts;
}

export function describeFormula(formula) {
  return formulaParts(formula)
    .map((part) => part.text)
    .join("");
}

/**
 * How one station of a formula gave its part of a value of the formula: the readings used (interpolated between
 * two readings when the delayed time falls between them), its trend, the factor that trend selected and the result.
 *
 * @param {Object} term A term of the formula.
 * @param {Array} readings parseGaugeReadings() of its gauge, oldest first.
 * @param {number} time Time of the formula value (parseReadingTime), usually the latest one.
 * @param {number} count Number of recent readings to list.
 * @return {Object} rows: newest first, with used on the readings used and { separator: true } where older readings
 *     are skipped to reach the ones used; value, trend, side ("up" / "down"), factor and contribution (null when
 *     the station has no value at that time).
 */
export function explainTermValue(term, readings, time, count = 10) {
  const normalized = normalizeTerm(term);
  const at = Number.isFinite(time) ? time - normalized.lag_hours * HOUR_MS : NaN;
  const value = flowAt(readings, at);

  let usedTimes = [];
  if (value !== null) {
    const exact = readings.find((reading) => reading.t === at);
    const last = readings[readings.length - 1];
    if (exact) {
      usedTimes = [exact.t];
    } else if (at > last.t) {
      usedTimes = [last.t];
    } else {
      const afterIndex = readings.findIndex((reading) => reading.t > at);
      usedTimes = [readings[afterIndex - 1].t, readings[afterIndex].t];
    }
  }

  const newestFirst = [...readings].reverse().map((reading) => ({
    date: reading.date,
    hour: reading.hour,
    flow: reading.flow,
    used: usedTimes.includes(reading.t),
  }));
  let rows = newestFirst.slice(0, count);
  const lastUsedIndex = newestFirst.findLastIndex((row) => row.used);
  if (lastUsedIndex >= count) {
    // The readings used are older (delay): the newest ones, then the ones around the readings used
    const firstUsedIndex = newestFirst.findIndex((row) => row.used);
    const head = Math.max(3, count - (lastUsedIndex - firstUsedIndex + 5));
    const from = Math.max(head, firstUsedIndex - 2);
    rows = [...newestFirst.slice(0, head), { separator: true }, ...newestFirst.slice(from, lastUsedIndex + 3)];
  }

  if (value === null) {
    return { rows, value: null, interpolated: false, trend: null, side: null, factor: null, contribution: null };
  }
  const previous = flowAt(readings, at - TREND_WINDOW_MS);
  const trend = previous === null ? 0 : value - previous;
  const side = trend >= 0 ? "up" : "down";
  const factor = side === "up" ? normalized.factor_up : normalized.factor_down;
  const sign = normalized.operator === "-" ? -1 : 1;
  return {
    rows,
    value,
    interpolated: usedTimes.length > 1,
    trend,
    side,
    factor,
    contribution: sign * factor * value,
  };
}
